import type { MerchantStatus, SupplyKey } from '../api/types';
import type { AppState } from '../state/store';
import { versionedAssetUrl } from '../assets/version';
import { btn, esc } from './components';

/**
 * Shared "Reward supplies / Reward loadout" panel.
 *
 * One component for the Dashboard (under Descend) and the Merchant (Reward
 * loadout): a Free Base Kit strip, a per-run maximum strip, and the paid-extra
 * +/- selector rows. All quantities come from the backend Merchant status
 * (base kit, caps, stock) — nothing here is hardcoded balance. Selecting
 * extras only stages them client-side; the Reward start still validates and
 * consumes stock server-side, and Echo runs use practice supplies.
 */

export const LOADOUT_SUPPLIES: Array<{ key: SupplyKey; label: string; asset: string }> = [
  { key: 'oil', label: 'Oil Flask', asset: '/assets/sprites/items/consumables/supply_oil_flask_01.png' },
  { key: 'torch', label: 'Torch Bundle', asset: '/assets/sprites/items/consumables/supply_torch_bundle_01.png' },
  { key: 'potion', label: 'Healing Draught', asset: '/assets/sprites/items/consumables/supply_healing_draught_01.png' },
];

type Counts = { oil: number; torch: number; potion: number };

function supplyFrame(entry: (typeof LOADOUT_SUPPLIES)[number], count: number): string {
  return `
    <span class="supply-frame" title="${esc(`${count}× ${entry.label}`)}" aria-label="${esc(`${count} ${entry.label}`)}">
      <span class="supply-frame__count">${esc(count)}&times;</span>
      <img src="${esc(versionedAssetUrl(entry.asset))}" alt="" width="34" height="34" loading="lazy" decoding="async">
    </span>`;
}

function supplyFrameStrip(counts: Counts): string {
  return `<div class="supply-frame-strip">${LOADOUT_SUPPLIES.map((s) => supplyFrame(s, counts[s.key])).join('')}</div>`;
}

/** Extras staged for the next Reward run, clamped to stock and per-run caps. */
export function clampedLoadoutExtras(state: AppState, merchant: MerchantStatus): Counts {
  return {
    oil: Math.min(state.rewardLoadoutExtras.oil, Math.min(merchant.supplies.oil, merchant.extraCaps.oil)),
    torch: Math.min(state.rewardLoadoutExtras.torch, Math.min(merchant.supplies.torch, merchant.extraCaps.torch)),
    potion: Math.min(state.rewardLoadoutExtras.potion, Math.min(merchant.supplies.potion, merchant.extraCaps.potion)),
  };
}

function extraSelectorRow(
  entry: (typeof LOADOUT_SUPPLIES)[number],
  merchant: MerchantStatus,
  extras: Counts,
): string {
  const key = entry.key;
  const stock = merchant.supplies[key];
  const extra = extras[key];
  const maxExtra = Math.max(0, Math.min(stock, merchant.extraCaps[key]));
  const enteringWith = merchant.baseKit[key] + extra;
  return `
    <div class="loadout-row" data-loadout-supply="${esc(key)}">
      <span class="supply-frame supply-frame--plain" title="${esc(entry.label)}">
        <img src="${esc(versionedAssetUrl(entry.asset))}" alt="" width="34" height="34" loading="lazy" decoding="async">
      </span>
      <div class="loadout-row__label">
        <strong>${esc(entry.label)}</strong>
        <span class="muted">entering run with ${esc(enteringWith)} / ${esc(merchant.totalCaps[key])}</span>
      </div>
      <div class="stepper">
        ${btn('-', 'loadout-adjust', { supply: key, delta: '-1' }, { variant: 'ghost', disabled: extra <= 0 })}
        <span class="stepper__count" aria-label="${esc(`${extra} paid extra ${entry.label}`)}">${esc(extra)}</span>
        ${btn('+', 'loadout-adjust', { supply: key, delta: '1' }, { variant: 'ghost', disabled: extra >= maxExtra })}
      </div>
      <span class="loadout-row__owned muted">Owned: ${esc(stock)}</span>
    </div>`;
}

/**
 * The shared panel body. `heading` differs per surface ("Reward supplies" on
 * the Dashboard, "Reward loadout" at the Merchant). The Merchant renders the
 * informational variant (`interactive: false`): only the Free Base Kit and
 * per-run maximum strips — no paid-extra steppers, Owned counts, or hint copy.
 * The Dashboard (under Descend) keeps the full interactive selector.
 */
export function rewardLoadoutPanel(
  state: AppState,
  heading = 'Reward supplies',
  opts: { interactive?: boolean } = {},
): string {
  const interactive = opts.interactive ?? true;
  const merchant = state.merchant;
  if (!merchant) {
    return `
      <div class="loadout-panel">
        <h3>${esc(heading)}</h3>
        <p class="muted">Load Merchant stock to see your base kit${interactive ? ' and paid extras' : ''}.</p>
        <div class="btn-row">${btn('Load Merchant stock', 'merchant-refresh', {}, { variant: 'ghost' })}${interactive ? btn('Open Merchant', 'goto-merchant', {}, { variant: 'ghost' }) : ''}</div>
      </div>`;
  }
  const kits = `
      <div class="loadout-kits">
        <div class="loadout-kit">
          <h4>Free Base Kit <span class="muted">(every run)</span></h4>
          ${supplyFrameStrip(merchant.baseKit)}
        </div>
        <div class="loadout-kit">
          <h4>Maximum supplies for 1 run</h4>
          ${supplyFrameStrip(merchant.totalCaps)}
        </div>
      </div>`;
  if (!interactive) {
    return `
    <div class="loadout-panel loadout-panel--info">
      <h3>${esc(heading)}</h3>
      ${kits}
    </div>`;
  }
  const extras = clampedLoadoutExtras(state, merchant);
  return `
    <div class="loadout-panel">
      <h3>${esc(heading)}</h3>
      ${kits}
      <div class="loadout-grid">
        <h4>Paid extras <span class="muted">(auto-loaded from your stock at Reward start)</span></h4>
        ${LOADOUT_SUPPLIES.map((entry) => extraSelectorRow(entry, merchant, extras)).join('')}
      </div>
      <p class="hint">Extras spend account stock when the Reward run starts. Echo runs use practice supplies and never consume stock.</p>
    </div>`;
}
